import React, { useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { IconButton } from '@/components/icon-button';
import { DownloadIcon, LoadingIcon } from '@/components/fun-ui/icons';
import { formatTime } from '@/lib/utils';

interface ImageInfo {
  url: string; // 图片URL
  name: string; // 可选的自定义文件名
}

interface ImageDownloaderProps {
  images: ImageInfo[]; // 可以接受对象数组或字符串数组
  zipName?: string; // 自定义ZIP文件名
}

const ImageDownloader: React.FC<ImageDownloaderProps> = ({
  images,
  zipName = 'images',
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);


  const getFileName = (customName: string): string => {
    const timemap = formatTime(Date.now(), 'YYYYMMDDHHmmss')
    return `${customName}_${timemap}.jpg`;
  };

  const downloadImagesAsZip = async () => {

    setIsLoading(true);
    setProgress(0);

    try {
      const zip = new JSZip();
      const imgFolder = zip.folder(zipName);

      // 获取所有图片的Blob
      const fetchPromises = images.map(async (image, index) => {
        try {
          const response = await fetch(image.url);
          if (!response.ok) throw new Error(`Failed to fetch ${image.url}`);
          const blob = await response.blob();

          const fileName = getFileName(image.name);

          if (imgFolder) {
            imgFolder.file(fileName, blob);
          }

          // 更新进度
          setProgress(((index + 1) / images.length) * 100);
        } catch (error) {
          console.error(`Error fetching image ${image.url}:`, error);
          return null;
        }
      });

      await Promise.all(fetchPromises);

      // 生成ZIP文件 - 使用正确的OnUpdateCallback类型
      const content = await zip.generateAsync({ type: 'blob' }, (metadata) => {
        setProgress(metadata.percent);
      });

      // 下载ZIP文件
      saveAs(content, `${zipName}.zip`);
    } catch (error) {
      console.error('Error creating zip file:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const hasImages = images.length > 0;

  if (!hasImages) {
    return <></>
  }

  return (
    <IconButton
      onClick={downloadImagesAsZip}
      disabled={isLoading || !hasImages}
      label={isLoading ? '打包中...' : `下载图片（${images.length}张）`}
      className=""
    >
      {isLoading ? <LoadingIcon /> : <DownloadIcon />}
    </IconButton>
  );
};

export default ImageDownloader;